import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useMemo,
  useEffect,
} from "react";
import { TInstitute } from "../apis/institute/institute.interfaces";
import { localStorageGet, localStorageSet } from "../utils/localStorage";

type TInstituteContextValue = {
  selectedInstitute: TInstitute | null;
  setSelectedInstitute: (institute: TInstitute | null) => void;
};

const InstituteContext = createContext<TInstituteContextValue | undefined>(
  undefined,
);

export const InstituteProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selectedInstitute, setSelectedInstituteState] =
    useState<TInstitute | null>(() => {
      const stored = localStorageGet("selectedInstitute");
      return stored ? (stored as TInstitute) : null;
    });

  const setSelectedInstitute = (institute: TInstitute | null) => {
    setSelectedInstituteState(institute);
    if (institute) {
      localStorageSet("selectedInstitute", institute);
    } else {
      localStorage.removeItem("selectedInstitute");
    }
  };

  const contextValue = useMemo<TInstituteContextValue>(
    () => ({
      selectedInstitute,
      setSelectedInstitute,
    }),
    [selectedInstitute],
  );

  return (
    <InstituteContext.Provider value={contextValue}>
      {children}
    </InstituteContext.Provider>
  );
};

export const useInstituteContext = () => {
  const context = useContext(InstituteContext);
  if (context === undefined) {
    throw new Error(
      "useInstituteContext must be used within an InstituteProvider",
    );
  }
  return context;
};
